<script setup lang="ts">
import { GpAvatar, GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { inject, onMounted, ref } from 'vue';
import { BillingProfileInject } from '../symbols';

const ROWS_PER_PAGE = 5;

interface ProjectsTableRow extends GpTableDataRow {
  project: string;
  name: string;
  avatarUrl: string;
}

const billingprofile = inject(BillingProfileInject);

const data = ref<Array<ProjectsTableRow>>([]);
const page = ref(0);
const maxPages = ref(0);

const isLoading = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

const userStore = useUserStore();
const reload = async () => {
  isLoading.value = true;
  try {
    const response = await userStore.reloadBillingProfileProjects({
      page: page.value + 1,
      itemsPerPage: ROWS_PER_PAGE,
      id: billingprofile.value.id,
    });

    data.value = response.projects.map((p) => ({
      key: p.id,
      project: p.id,
      name: p.name,
      avatarUrl: p.avatarUrl,
    }));

    maxPages.value = response.pagination.total;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = (error as ServiceError).message;
  } finally {
    isLoading.value = false;
  }
};

const handlePagination = (newPage: number) => {
  page.value = newPage;
  reload();
};

onMounted(async () => {
  reload();
});
</script>

<template>
  <div>
    <p class="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-font-bold">Assigned projects</p>
    <GpTable
      :data="data"
      pagination
      :page="page"
      :max-pages="maxPages"
      :rows-per-page="ROWS_PER_PAGE"
      :is-loading="isLoading"
      loading-message="Loading projects ..."
      empty-message="No assigned projects found."
      @update:page="handlePagination"
    >
      <GpTableColumn label="Project" field="project">
        <template
          #body="{ data, row }: { data: ProjectsTableRow['project']; row: ProjectsTableRow }"
        >
          <div class="tw-flex tw-items-center">
            <GpAvatar
              :src="row.avatarUrl"
              :alt="row.name"
              custom-size
              class="tw-mr-4"
              style="--gp-avatar-size: 32px"
            />

            <router-link :to="{ name: 'ProjectSettings', params: { projectId: data } }">
              {{ row.name }}
            </router-link>
          </div>
        </template>
      </GpTableColumn>
    </GpTable>
  </div>
</template>

<style scoped></style>
